import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Lock, ShieldCheck, PackageCheck } from "lucide-react";
import { toast } from "sonner";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

const init = {
    full_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    payment_method: "cod",
    notes: "",
};

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const existing = document.querySelector(
            `script[src="${RAZORPAY_SCRIPT}"]`,
        );
        if (existing) {
            existing.addEventListener("load", () => resolve(true));
            existing.addEventListener("error", () => resolve(false));
            return;
        }
        const s = document.createElement("script");
        s.src = RAZORPAY_SCRIPT;
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

export default function Checkout() {
    const nav = useNavigate();
    const { items, subtotal, shipping, total, clearCart } = useCart();
    const [f, setF] = useState(init);
    const [loading, setLoading] = useState(false);
    const [config, setConfig] = useState({ razorpay_enabled: false });

    useEffect(() => {
        api.get("/config")
            .then((r) => setConfig(r.data))
            .catch(() => setConfig({ razorpay_enabled: false }));
    }, []);

    const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

    if (items.length === 0) {
        return (
            <div
                className="max-w-[900px] mx-auto px-6 md:px-12 pt-24 pb-32 text-center"
                data-testid="checkout-empty"
            >
                <p
                    className="font-serif text-3xl"
                    style={{ color: "var(--vl-text)" }}
                >
                    Your bag is empty.
                </p>
                <Link
                    to="/shop"
                    className="vl-btn vl-btn-solid mt-8 inline-flex"
                >
                    Browse the collection
                </Link>
            </div>
        );
    }

    const launchRazorpay = async (orderNumber) => {
        const loaded = await loadRazorpayScript();
        if (!loaded) {
            toast.error("Could not load payment gateway. Please try COD.");
            return false;
        }
        let rzp;
        try {
            const { data: rz } = await api.post(
                "/payment/razorpay/order",
                { amount: total, order_number: orderNumber },
            );
            rzp = rz;
        } catch (err) {
            toast.error(
                "Payment gateway unavailable. Please use Cash on Delivery.",
            );
            return false;
        }

        return new Promise((resolve) => {
            const options = {
                key: rzp.key_id,
                amount: rzp.amount,
                currency: rzp.currency,
                order_id: rzp.razorpay_order_id,
                name: "Veloura Wellness",
                description: "Private intimate wellness order",
                image: "",
                prefill: {
                    name: f.full_name,
                    email: f.email,
                    contact: f.phone,
                },
                theme: { color: "#B76E79" },
                notes: { order_number: orderNumber },
                handler: async (response) => {
                    try {
                        await api.post("/payment/razorpay/verify", {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id:
                                response.razorpay_payment_id,
                            razorpay_signature:
                                response.razorpay_signature,
                            order_number: orderNumber,
                        });
                        toast.success("Payment received. Thank you.");
                        resolve(true);
                    } catch {
                        toast.error(
                            "Payment verification failed. Our concierge will reach out.",
                        );
                        resolve(false);
                    }
                },
                modal: {
                    ondismiss: () => {
                        toast("Payment cancelled. Your order is unpaid.");
                        resolve(false);
                    },
                },
            };
            const r = new window.Razorpay(options);
            r.on("payment.failed", () => {
                toast.error("Payment failed. Please try again.");
                resolve(false);
            });
            r.open();
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...f,
                items: items.map((i) => ({
                    product_id: i.product_id,
                    name: i.name,
                    price: i.price,
                    quantity: i.quantity,
                    image: i.image,
                })),
                subtotal,
                shipping,
                total,
            };
            const { data } = await api.post("/orders", payload);

            if (f.payment_method === "razorpay") {
                const ok = await launchRazorpay(data.order_number);
                if (!ok) {
                    setLoading(false);
                    return;
                }
            } else {
                toast.success(
                    "Order placed. A concierge note is on its way.",
                );
            }
            clearCart();
            nav(`/order-confirmation/${data.order_number}`);
        } catch (err) {
            toast.error("Please check your details and try again.");
        } finally {
            setLoading(false);
        }
    };

    const paymentOptions = [
        {
            v: "cod",
            t: "Cash on Delivery",
            d: "Pay privately, in cash, at your door.",
            enabled: true,
        },
        {
            v: "razorpay",
            t: "UPI · Card · Netbanking",
            d: config.razorpay_enabled
                ? "Secured by Razorpay. Your card details never touch our servers."
                : "Live payments launching soon. Please use Cash on Delivery for now.",
            enabled: config.razorpay_enabled,
        },
    ];

    return (
        <div
            className="max-w-[1200px] mx-auto px-6 md:px-12 pt-16 pb-32"
            data-testid="checkout-page"
        >
            <div className="overline mb-4">Private checkout</div>
            <h1
                className="font-serif text-4xl sm:text-5xl tracking-tight mb-12"
                style={{ color: "var(--vl-text)" }}
            >
                A few{" "}
                <em
                    style={{
                        color: "var(--vl-rose)",
                        fontStyle: "italic",
                    }}
                >
                    quiet details.
                </em>
            </h1>

            <form
                onSubmit={submit}
                className="grid lg:grid-cols-12 gap-10"
                data-testid="checkout-form"
            >
                <div className="lg:col-span-8 space-y-10">
                    <fieldset>
                        <legend className="overline mb-6">
                            01 · Contact
                        </legend>
                        <div className="grid sm:grid-cols-2 gap-6">
                            <input
                                required
                                placeholder="Full name"
                                className="vl-input"
                                value={f.full_name}
                                onChange={(e) =>
                                    set("full_name", e.target.value)
                                }
                                data-testid="co-name"
                            />
                            <input
                                required
                                type="email"
                                placeholder="Email (for order updates)"
                                className="vl-input"
                                value={f.email}
                                onChange={(e) =>
                                    set("email", e.target.value)
                                }
                                data-testid="co-email"
                            />
                            <input
                                required
                                placeholder="Phone (private)"
                                className="vl-input"
                                value={f.phone}
                                onChange={(e) =>
                                    set("phone", e.target.value)
                                }
                                data-testid="co-phone"
                            />
                        </div>
                    </fieldset>

                    <fieldset>
                        <legend className="overline mb-6">
                            02 · Shipping address
                        </legend>
                        <div className="grid gap-6">
                            <input
                                required
                                placeholder="Address line 1"
                                className="vl-input"
                                value={f.address_line1}
                                onChange={(e) =>
                                    set("address_line1", e.target.value)
                                }
                                data-testid="co-addr1"
                            />
                            <input
                                placeholder="Address line 2 (optional)"
                                className="vl-input"
                                value={f.address_line2}
                                onChange={(e) =>
                                    set("address_line2", e.target.value)
                                }
                                data-testid="co-addr2"
                            />
                            <div className="grid sm:grid-cols-3 gap-6">
                                <input
                                    required
                                    placeholder="City"
                                    className="vl-input"
                                    value={f.city}
                                    onChange={(e) =>
                                        set("city", e.target.value)
                                    }
                                    data-testid="co-city"
                                />
                                <input
                                    required
                                    placeholder="State"
                                    className="vl-input"
                                    value={f.state}
                                    onChange={(e) =>
                                        set("state", e.target.value)
                                    }
                                    data-testid="co-state"
                                />
                                <input
                                    required
                                    placeholder="PIN code"
                                    className="vl-input"
                                    value={f.pincode}
                                    onChange={(e) =>
                                        set("pincode", e.target.value)
                                    }
                                    data-testid="co-pincode"
                                />
                            </div>
                        </div>
                    </fieldset>

                    <fieldset>
                        <legend className="overline mb-6">
                            03 · Payment
                        </legend>
                        <div className="space-y-3">
                            {paymentOptions.map((opt) => (
                                <label
                                    key={opt.v}
                                    className={`flex items-start gap-4 p-4 ${opt.enabled ? "cursor-pointer" : "cursor-not-allowed"}`}
                                    style={{
                                        border:
                                            "1px solid var(--vl-border)",
                                        background:
                                            f.payment_method === opt.v
                                                ? "var(--vl-surface)"
                                                : "transparent",
                                        opacity: opt.enabled ? 1 : 0.6,
                                    }}
                                    data-testid={`co-pay-${opt.v}`}
                                >
                                    <input
                                        type="radio"
                                        name="pay"
                                        disabled={!opt.enabled}
                                        checked={
                                            f.payment_method === opt.v
                                        }
                                        onChange={() =>
                                            set("payment_method", opt.v)
                                        }
                                        className="mt-1 accent-[var(--vl-rose)]"
                                    />
                                    <div>
                                        <div
                                            className="text-sm"
                                            style={{
                                                color: "var(--vl-text)",
                                            }}
                                        >
                                            {opt.t}
                                            {!opt.enabled && (
                                                <span
                                                    className="ml-2 text-[10px] tracking-[0.22em] uppercase"
                                                    style={{
                                                        color: "var(--vl-rose)",
                                                    }}
                                                >
                                                    Coming soon
                                                </span>
                                            )}
                                        </div>
                                        <div
                                            className="text-xs mt-1"
                                            style={{
                                                color: "var(--vl-muted)",
                                            }}
                                        >
                                            {opt.d}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                        <textarea
                            placeholder="Delivery notes (optional) — e.g. 'Leave at door'"
                            className="vl-input mt-6"
                            value={f.notes}
                            onChange={(e) => set("notes", e.target.value)}
                            data-testid="co-notes"
                            rows={3}
                            style={{ resize: "vertical" }}
                        />
                    </fieldset>
                </div>

                <aside
                    className="lg:col-span-4 p-8 h-fit sticky top-24"
                    style={{
                        background: "var(--vl-surface)",
                        border: "1px solid var(--vl-border)",
                    }}
                >
                    <div className="overline mb-4">Your order</div>
                    <ul className="space-y-3 text-sm mb-6">
                        {items.map((i) => (
                            <li
                                key={i.product_id}
                                className="flex justify-between gap-4"
                            >
                                <span style={{ color: "var(--vl-text)" }}>
                                    {i.name} × {i.quantity}
                                </span>
                                <span style={{ color: "var(--vl-muted)" }}>
                                    {formatINR(i.price * i.quantity)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <div
                        className="hairline mb-4"
                        style={{
                            background: "var(--vl-border)",
                            height: 1,
                        }}
                    />
                    <div className="flex justify-between py-1 text-sm">
                        <span style={{ color: "var(--vl-muted)" }}>
                            Subtotal
                        </span>
                        <span style={{ color: "var(--vl-text)" }}>
                            {formatINR(subtotal)}
                        </span>
                    </div>
                    <div className="flex justify-between py-1 text-sm">
                        <span style={{ color: "var(--vl-muted)" }}>
                            Shipping
                        </span>
                        <span style={{ color: "var(--vl-text)" }}>
                            {shipping === 0
                                ? "Complimentary"
                                : formatINR(shipping)}
                        </span>
                    </div>
                    <div
                        className="hairline my-3"
                        style={{
                            background: "var(--vl-border)",
                            height: 1,
                        }}
                    />
                    <div className="flex justify-between items-baseline">
                        <span className="overline">Total</span>
                        <span
                            className="font-serif text-3xl"
                            style={{ color: "var(--vl-text)" }}
                            data-testid="co-total"
                        >
                            {formatINR(total)}
                        </span>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="vl-btn vl-btn-solid w-full justify-center mt-8"
                        data-testid="co-submit"
                    >
                        {loading
                            ? "Placing order…"
                            : f.payment_method === "razorpay"
                              ? `Pay ${formatINR(total)}`
                              : "Place order"}
                    </button>
                    <div
                        className="mt-6 space-y-2 text-[11px]"
                        style={{ color: "var(--vl-muted)" }}
                    >
                        <div className="flex items-center gap-2">
                            <Lock size={12} strokeWidth={1.25} />
                            Private checkout — your data is never shared.
                        </div>
                        <div className="flex items-center gap-2">
                            <ShieldCheck size={12} strokeWidth={1.25} />
                            Unbranded packaging.
                        </div>
                        <div className="flex items-center gap-2">
                            <PackageCheck size={12} strokeWidth={1.25} />
                            Return label reads <em>V. Atelier</em>.
                        </div>
                    </div>
                </aside>

                {/* Mobile sticky action bar */}
                <div
                    className="vl-mobile-action lg:hidden col-span-full -mx-6 md:-mx-12"
                    data-testid="checkout-mobile-bar"
                >
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <div
                                className="text-[10px] tracking-[0.22em] uppercase"
                                style={{ color: "var(--vl-muted)" }}
                            >
                                Total
                            </div>
                            <div
                                className="font-serif text-xl"
                                style={{ color: "var(--vl-text)" }}
                            >
                                {formatINR(total)}
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="co-submit-mobile"
                            className="vl-btn vl-btn-solid flex-1 justify-center"
                        >
                            {loading
                                ? "Placing…"
                                : f.payment_method === "razorpay"
                                  ? "Pay now"
                                  : "Place order"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
